import React from "react";
import { CONSTRUCTION_LIFT } from "../map/animation/constructionTiming";

/** Shape position in map units; shape ids change on every rebuild. */
const arrivalKey = (node: SVGGElement) => {
  const box = node.getBBox();
  return [
    node.dataset.constructionShape,
    Math.round(box.x / 4),
    Math.round(box.y / 4),
    Math.round(box.width / 4),
    Math.round(box.height / 4)
  ].join(":");
};

/**
 * Animates only the shapes that were not on screen before, so a map that keeps
 * growing (a live import) shows each new desk dropping in. `seen` must outlive
 * remounts of the preview; the caller owns it and clears it for a new import.
 */
export function useArrivalAnimation(
  ref: React.RefObject<SVGSVGElement | null>,
  value: unknown,
  seen: React.RefObject<Set<string>> | undefined
) {
  React.useLayoutEffect(() => {
    const svg = ref.current;
    if (
      !svg ||
      !seen?.current ||
      !value ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const memory = seen.current;
    const animate = () => {
      const nodes = Array.from(
        svg.querySelectorAll<SVGGElement>("[data-construction-shape]")
      ).filter((node) => node.dataset.constructionLayer !== "background");
      if (!nodes.length) return false;
      let arrivals = 0;
      for (const node of nodes) {
        const key = arrivalKey(node);
        if (memory.has(key)) continue;
        memory.add(key);
        node.animate?.(
          [
            {
              transform: `translateY(-${CONSTRUCTION_LIFT / 2}px)`,
              opacity: 0
            },
            { transform: "translateY(0px)", opacity: 1 }
          ],
          {
            duration: 450,
            // Stagger a burst of arrivals, but never make one wait long.
            delay: Math.min(arrivals++ * 25, 1200),
            easing: "cubic-bezier(0.16, 1, 0.3, 1)",
            fill: "backwards"
          }
        );
      }
      return true;
    };
    // MapCanvasProvider hydrates shapes in an effect after this SVG mounts.
    if (animate()) return;
    const observer = new MutationObserver(() => {
      if (animate()) observer.disconnect();
    });
    observer.observe(svg, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [ref, value, seen]);
}
